import { useEffect, useState } from 'react';
import { patrolApi } from '../services/portalApi';
import { fmtDateTime } from '../utils/date';
import type { ActiveGuard } from '../types';

interface Props {
  guards: ActiveGuard[];
  connected: boolean;
}

export default function MapStatusBar({ guards, connected }: Props) {
  const [lastPatrolTime, setLastPatrolTime] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch when there are no active guards (otherwise it's not relevant)
    if (guards.length > 0) { setLastPatrolTime(null); return; }
    patrolApi.list(1).then((result) => {
      const last = result.data.find((p) => p.ended_at);
      if (last?.ended_at) {
        setLastPatrolTime(fmtDateTime(last.ended_at));
      }
    }).catch(() => {});
  }, [guards.length]);

  return (
    <div className="flex items-center justify-between px-6 py-4 bg-white border-b flex-shrink-0">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">Live Map</h1>
        {guards.length === 0 ? (
          <p className="text-sm text-gray-500">
            No active patrols
            {lastPatrolTime && (
              <span className="ml-2 text-gray-400">· Last patrol: {lastPatrolTime}</span>
            )}
          </p>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-gray-500">
              {guards.length} active patrol{guards.length !== 1 ? 's' : ''}
            </p>
            {guards.map((g) => (
              <span key={g.guard_id} className="flex items-center gap-1.5 text-sm">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: g.colour }} />
                <span className="font-medium text-gray-700">{g.guard_name}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-400 animate-pulse'}`} />
        <span className="text-xs text-gray-500">{connected ? 'Live' : 'Reconnecting…'}</span>
      </div>
    </div>
  );
}
